<template lang="pug">
.v-container.suus__attend__hero.pa-4(fluid, v-if="hasErrorInEventOrOwnUserStatus")
  v-row.my-6(justify="center", no-gutters)
    v-col(cols="12", md="10")
      v-card.pa-8
        ErrorHero(
          :clickHandler="onBtnClick",
          :titleText="titleText",
          :line1="line1",
          :line2="line2",
          :btnLabel="btnLabel"
        )
.v-container.suus__attend__hero.pa-4(fluid, v-else)
  v-row.mb-4
    v-col.d-none.d-sm-flex(cols="12", sm="2")
      Digest(:width="digestWidth")
    v-col(cols="12", sm="10")
      Header(
        :text="headerText.line1",
        :bold="false",
        color="white",
        customClass="my-0",
        customSkeletonClass="mt-4 mb-4",
        :level="titleLevel",
        :isLoading="loading"
      )
      Header(
        :text="headerText.line2",
        :bold="false",
        color="white",
        customClass="my-0",
        customSkeletonClass="mb-2",
        :level="titleLevel",
        :isLoading="loading"
      )
    v-col(cols="12")
      AttendCard
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance } from "vue"
import { EVENT_STATUS } from "constants/constants"
import { createNamespacedHelpers } from "vuex"
const { mapState, mapActions, mapGetters } = createNamespacedHelpers("event")
import Digest from "../graphics/Digest.vue"
import Header from "../UI/Header.vue"
import ErrorHero from "../UI/ErrorHero.vue"
import AttendCard from "./AttendCard.vue"
import GuestModal from "./GuestModal.vue"
import { Event } from "suus-api"
import { provideOwnUserAttendanceContext } from "app/modules/events/use-own-user-attendance-context.concern"
import store from "store"

export default defineComponent({
  name: "AttendPage",
  components: { Digest, Header, AttendCard, GuestModal, ErrorHero },
  computed: {
    titleText(): string {
      return this.$t("components.event.error.titleText").toString()
    },
    line1(): string {
      return this.$t("components.event.error.line1").toString()
    },
    line2(): string {
      return this.$t("components.event.error.line2").toString()
    },
    btnLabel(): string {
      return this.$t("components.event.error.btnLabel").toString()
    },
    loading(): boolean {
      return this.eventLoading || this.ownUserStatusLoading
    },
    headerTextOnAttend(): { line1: string; line2: string } {
      let line1 = this.$t("components.attend.headerText.line1")
      if (this.ownUserName) line1 = `${line1} ${this.ownUserName}`
      line1 = line1 + ","
      let line2 = this.$t(
        "components.attend.headerText.line2.REGISTER"
      ).toString()

      if (
        this.cardStatus === EVENT_STATUS.REGISTERED ||
        this.cardStatus === EVENT_STATUS.FINISHED_REGISTRATION
      ) {
        line2 = this.$t(
          "components.attend.headerText.line2.REGISTERED"
        ).toString()
      }
      if (this.cardStatus === EVENT_STATUS.DEREGISTERED) {
        line2 = this.$i18n
          .t("components.attend.headerText.line2.REGISTER")
          .toString()
      }
      if (this.cardStatus === EVENT_STATUS.EVENT_PASSED) {
        line2 = this.$t(
          "components.attend.headerText.line2.EVENT_PASSED"
        ).toString()
      }
      if (this.cardStatus === EVENT_STATUS.ATTEND_ONLINE_NOW) {
        line2 = this.$t(
          "components.attend.headerText.line2.ATTEND_ONLINE_NOW"
        ).toString()
      }
      return { line1, line2 }
    },
    headerTextOnDecline(): { line1: string; line2: string } {
      const line1 = `${this.$t("components.attend.headerText.line1")} ${
        this.ownUserName
      },`
      let line2 = this.$t(
        "components.attend.headerText.line2.DECLINE"
      ).toString()
      if (this.cardStatus === EVENT_STATUS.DEREGISTERED) {
        line2 = this.$i18n
          .t("components.attend.headerText.line2.DEREGISTERED")
          .toString()
      }
      if (this.cardStatus === EVENT_STATUS.EVENT_PASSED) {
        line2 = this.$t(
          "components.attend.headerText.line2.EVENT_PASSED"
        ).toString()
      }
      return { line1, line2 }
    },
    headerText(): { line1: string; line2: string } {
      return this.isDecline ? this.headerTextOnDecline : this.headerTextOnAttend
    },
    isDecline(): boolean {
      return this.$route.name === "Decline"
    },
    titleLevel(): number {
      return this.$vuetify.breakpoint.name === "xs" ? 4 : 1
    },
    digestWidth(): string {
      return this.$vuetify.breakpoint.name === "xs" ? "30" : "80"
    },
    ...mapState({
      eventData: (state: any): Event => state.currentEvent.data,
      eventError: (state: any): any => state.currentEvent.error,
      eventLoading: (state: any): boolean => state.currentEvent.loading,
    }),
    ...mapGetters(["hasError"]),
    hasErrorInEventOrOwnUserStatus(): boolean {
      return this.hasError || !!this.ownUserStatusError
    },
  },
  methods: {
    onBtnClick(): void {
      window.open("/", "_self")
    },
    ...mapActions(["getEventFromDigest"]),
  },
  setup() {
    const currentInstance = getCurrentInstance()
    const eventId = currentInstance.proxy?.$route.params.event_id

    const event$ = store.dispatch("event/getEventFromDigest", eventId)
    const ownUserAttendanceConcern = provideOwnUserAttendanceContext(eventId)

    event$.then(() => {
      ownUserAttendanceConcern.initializeOwnUserStatusFromDigest()
    })

    return {
      cardStatus: ownUserAttendanceConcern.eventAttendStatus,
      ownUserStatusLoading: ownUserAttendanceConcern.ownUserStatusLoading,
      ownUserName: ownUserAttendanceConcern.ownUserName,
      ownUserStatusError: ownUserAttendanceConcern.ownUserStatusError,
    }
  },
})
</script>

<style lang="sass">
.suus__attend__hero
  max-width: 750px
  min-height: 75vh
  width: 100% !important
  margin: auto !important
</style>
